import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DriveItem,
  SharedWithUsers,
} from "../../../../../interfaces/caseFileDrive/DriveFiles";
import { useDriveContext } from "../../../../../contexts/DriveContext";
import { useAuth } from "../../../../../firebase/AuthProvider";
import { getUserFolderDataAsDriveItems, getUserFolderData } from "../../../../../services/fileServices/FileService";
import {
  downloadUserFile,
  removeFile,
  removeFolder,
} from "../../../../../services/fileServices/UserDriverService";
import {
  Button,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  ProgressBar,
  Spinner,
  Table,
} from "react-bootstrap";
import girdviewicon from "../../../../../assets/images/girdviewicon.svg";
import listviewicon from "../../../../../assets/images/listviewicon.svg";
import sharelinkicon from "../../../../../assets/images/sharelinkicon.svg";
import searchicon from "../../../../../assets/images/searchicon.svg";
import fileuploadicon from "../../../../../assets/images/fileuploadicon.svg";
import folderuploadicon from "../../../../../assets/images/folderuploadicon.svg";
import foldericon from "../../../../../assets/images/foldericon.svg";
import folderlgicon from "../../../../../assets/images/folderlgicon.svg";
import pdficon from "../../../../../assets/images/pdficon.svg";
import fileicons from "../../../../../assets/images/fileicons.svg";
import tabfoldericon from "../../../../../assets/images/tabfoldericon.svg";
import excelicon from "../../../../../assets/images/excelicon.svg";
import wordfileicon from "../../../../../assets/images/wordfileicon.svg";
import editicons from "../../../../../assets/images/editicons.svg";
import trashicons from "../../../../../assets/images/trashicons.svg";
import { formatFileSize } from "../../../../../utilities/fileUtils";
import { getTimeAgo } from "../../../../../utilities/dateUtils";
import { convertUtcToLocalDate } from "../../../../../utilities/dateUtils";
import FolderUploadModal from "./Modals/FolderUploadModal";
import FileUploadModal from "./Modals/FileUploadModal";
import ShareFileModal from "./Modals/ShareFileModal";
import "./personaltab.css";
import { toast } from "react-toastify";
import RenameFileFolder from "./RenameFileFolder";
import DriveStorage from "./DriveStorage";
import { showConfirmationDialog } from "../../../../modal/ConfimationModal/Confimation";
import UploadedFileShareModal from "./Modals/UploadedFileShareModal";
import ShowUsersModal from "./Modals/ShowUsersModal";
import AvatarImage from "../../../../ui/Avatar/AvatarImage";
import NoParticipantsFound from "../../../participantfiles/noParticipantsFound";

const extensionToIcon: { [key: string]: string } = {
  ".pdf": pdficon,
  ".png": fileicons,
  ".jpg": fileicons,
  ".jpeg": fileicons,
  ".xls": excelicon,
  ".xlsx": excelicon,
  ".doc": wordfileicon,
  ".docx": wordfileicon,
  ".txt": fileicons,
  ".csv": excelicon,
  ".zip": fileicons,
  ".rar": fileicons,
};

function PersonalDrive() {
  const { currentUser } = useAuth();
  const { folderId } = useParams();
  const navigate = useNavigate();
  const { searchTerm, setOnFileUploadClick, setOnFolderCreateClick, pagination, setPagination, setTotalItems, selectedFileIds, setSelectedFileIds, setRefreshCurrentFolder, setCurrentFolderId } = useDriveContext();
  const [authToken, setAuthToken] = useState<string>("");

  // The folder + items in the current view
  const [currentFolder, setCurrentFolder] = useState<{
    id: number;
    name: string;
    parentFolderId: number | null;
  } | null>(null);
  const [items, setItems] = useState<DriveItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [storageRefreshKey, setStorageRefreshKey] = useState(0);
  const isFetchingRef = useRef<boolean>(false);

  // --- Rename state
  const [renameTarget, setRenameTarget] = useState<DriveItem | null>(null);
  const [itemStructure, setItemStucture] = useState<"grid" | "list">("list");
  const [selectedFileForShare, setSelectedFileForShare] =
    useState<DriveItem | null>();
  const [selectedDriveItemToView, setSelectedDriveItemToView] =
    useState<DriveItem>();

  //   Modal State
  const [showFileModal, setShowFileModal] = useState<boolean>(false);
  const [showFolderModal, setShowFolderModal] = useState<boolean>(false);
  const [showSharedFileModal, setShowSharedFileModal] =
    useState<boolean>(false);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [showUploadedFileShareModal, setshowUploadedFileShareModal] =
    useState<boolean>(false);
  const [showUsersModal, setShowUsersModal] = useState<boolean>(false);
  const [breadcrumbs, setBreadcrumbs] = useState<
    { id: number | null; name: string }[]
  >([]);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const shareTriggerRef = useRef<HTMLElement | null>(null);
  const [sharePosition, setSharePosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [openShareFileId, setOpenShareFileId] = useState<string | null>(null);
  const [rootLevelFiles, setRootLevelFiles] = useState<DriveItem[]>([]);

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Multi-select state: only files (not folders) can be selected
  const toggleSelectFile = (fileId: string) => {
    setSelectedFileIds((prev) =>
      prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    );
  };

  // Keep selected IDs in sync with current items (remove any ids that no longer exist)
  useEffect(() => {
    setSelectedFileIds((prev) => prev.filter((id) => items.some((it) => it.id === id)));
  }, [items]);

  const openShareModal = (item: DriveItem) => {
    // This ensures only one modal is open at a time
    setOpenShareFileId(item.id);
    setSelectedFileForShare(item);
  };

  // Fetch all root-level files (files not in any folder)
  const fetchRootLevelFiles = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const pageSize = 100; // Use a reasonable page size that the API accepts
      let allFiles: DriveItem[] = [];
      let pageNumber = 1;
      let hasMorePages = true;

      // Fetch all pages of root folder files
      while (hasMorePages) {
        const response = await getUserFolderData(token, {
          folderId: null, // Root folder
          pageNumber: pageNumber,
          pageSize: pageSize,
        });

        // Map files directly from the response (these are files in root folder, i.e., not in any subfolder)
        const fileItems: DriveItem[] = (response.files || []).map((fi) => ({
          id: fi.id.toString(),
          name: fi.name,
          isFolder: false,
          size: fi.size,
          mimeType: fi.mimeType,
          updateDate: fi.createdAt,
          authorFullName: fi.authorFullName,
          authorId: fi.authorId,
          sharedWithUsers: fi.sharedWithUsers,
          createdAt: fi.createdAt,
        }));

        allFiles = [...allFiles, ...fileItems];

        // Check if there are more pages to fetch
        const totalItems = response.count || 0;
        const fetchedSoFar = allFiles.length;
        hasMorePages = fetchedSoFar < totalItems && fileItems.length === pageSize;

        if (hasMorePages) {
          pageNumber++;
        }
      }

      setRootLevelFiles(allFiles);
    } catch (error) {
      console.error("Error fetching root level files:", error);
      setRootLevelFiles([]);
    }
  }, [currentUser]);



  // Refresh root files when folder modal opens

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    setSelectedFileForShare(null);
  };

  const refreshDriveStorage = () => setStorageRefreshKey((prev) => prev + 1);

  const refreshCurrentFolder = useCallback(async () => {
    if (!currentUser) return;

    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      const token = await currentUser.getIdToken();
      const { currentFolder, items, count } =
        await getUserFolderDataAsDriveItems(
          token,
          folderId ?? null,
          pagination.pageNumber,
          pagination.pageSize
        );
      setCurrentFolder(currentFolder ?? null);
      setItems(items);
      setTotalItems(count);
      refreshDriveStorage();

      // Breadcrumb logic
      const path: { id: number | null; name: string }[] = [];
      let current:
        | { id: number; name: string; parentFolderId: number | null }
        | null
        | undefined = currentFolder;
      while (current) {
        path.unshift({ id: current.id, name: current.name });
        if (current.parentFolderId) {
          const { currentFolder: parent } = await getUserFolderDataAsDriveItems(
            token,
            current.parentFolderId.toString(),
            1,
            1
          );
          current = parent;
        } else {
          current = null;
        }
      }
      setBreadcrumbs([{ id: null, name: "Drive" }, ...path]);
    } catch (err) {
      console.error("Error refreshing folder:", err);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [currentUser, folderId, pagination.pageNumber, pagination.pageSize]);

  useEffect(() => {
    const getAuthtoken = async () => {
      if (!currentUser) return;
      const token = await currentUser.getIdToken();
      setAuthToken(token);
    };
    getAuthtoken();
    refreshCurrentFolder();
  }, [currentUser, folderId, pagination.pageNumber, pagination.pageSize, refreshCurrentFolder]);

  const handleDeleteItem = async (item: DriveItem) => {
    const isConfirm = await showConfirmationDialog();
    if (!isConfirm) return;
    if (item.isFolder) {
      try {
        await removeFolder(authToken, item.id);
        toast.success("Folder Deleted Successfully");
        refreshCurrentFolder(); // Refresh the folder view after successful deletion
      } catch (err: any) {
        toast.error(err.message);
      }
    } else {
      try {
        await removeFile(authToken, item.id);
        toast.success("File Deleted Successfully");
        refreshCurrentFolder(); // Refresh the folder view after successful deletion
      } catch (err: any) {
        toast.error(err.message);
      }
      // e.g. confirm => call service => refresh
    }
  };

  const handleDownloadFile = async (item: DriveItem) => {
    try {
      if (currentUser) {
        const authToken: string = await currentUser.getIdToken();
        const blob = await downloadUserFile(Number(item.id), authToken);
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = item.name; // Use the provided fileName
          document.body.appendChild(a);
          a.click();

          URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const filtered = items.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortItems = (itemsToSort: DriveItem[], column: string, direction: "asc" | "desc"): DriveItem[] => {
    const sorted = [...itemsToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (column) {
        case "name":
          aValue = a.name?.toLowerCase() || "";
          bValue = b.name?.toLowerCase() || "";
          break;
        case "sharedWith":
          aValue = a.sharedWithUsers?.length || 0;
          bValue = b.sharedWithUsers?.length || 0;
          break;
        case "created":
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        case "updated":
          const aUpdateDate = a.lastUpdatedDate || a.updateDate;
          const bUpdateDate = b.lastUpdatedDate || b.updateDate;
          aValue = aUpdateDate ? new Date(aUpdateDate).getTime() : 0;
          bValue = bUpdateDate ? new Date(bUpdateDate).getTime() : 0;
          break;
        case "size":
          aValue = a.size || 0;
          bValue = b.size || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const sortedAndFiltered = sortColumn
    ? sortItems(filtered, sortColumn, sortDirection)
    : filtered;

  function handleItemClick(item: DriveItem) {
    // If we’re currently renaming, we might exit rename mode here (optional).
    if (item.isFolder) {
      navigate(`/drive/personal/${item.id}`);
    } else {
      navigate(`/view/user/file/${item.id}`);
    }
  }

  const handleCloseFileModal = () => setShowFileModal(false);
  const handleCloseFolderModal = () => setShowFolderModal(false);
  const handleCloseSharedFileModal = () => setShowSharedFileModal(false);
  const handleCloseUsersModal = () => {
    setSelectedDriveItemToView(undefined);
    setShowUsersModal(false);
  };
  const handleCloseRenamerModal = () => {
    setShowRenameModal(false);
    setRenameTarget(null);
  };
  const handleCloseUploadedFileShareModal = () => {
    setshowUploadedFileShareModal(false);
    setSelectedFileForShare(null);
  };

  const handleShowFileModal = () => setShowFileModal(true);
  const handleShowFolderModal = useCallback(() => {
    // Fetch root level files (files not in folders) when opening the modal
    fetchRootLevelFiles();
    setShowFolderModal(true);
  }, [fetchRootLevelFiles]);

  // Register handlers with context so header button can use them
  useEffect(() => {
    if (setOnFileUploadClick) {
      setOnFileUploadClick(() => handleShowFileModal);
    }
    if (setOnFolderCreateClick) {
      setOnFolderCreateClick(() => handleShowFolderModal);
    }
    if (setRefreshCurrentFolder) {
      setRefreshCurrentFolder(() => refreshCurrentFolder);
    }
    // Cleanup on unmount
    return () => {
      if (setOnFileUploadClick) setOnFileUploadClick(undefined);
      if (setOnFolderCreateClick) setOnFolderCreateClick(undefined);
      if (setRefreshCurrentFolder) setRefreshCurrentFolder(undefined);
    };
  }, [setOnFileUploadClick, setOnFolderCreateClick, setRefreshCurrentFolder, handleShowFolderModal, refreshCurrentFolder]);

  // Set current folder ID in context
  useEffect(() => {
    setCurrentFolderId(folderId ? Number(folderId) : null);
  }, [folderId, setCurrentFolderId]);

  const handleShowSharedFileModal = () => setShowSharedFileModal(true);
  const handleShowUsersModal = (driveItem: DriveItem) => {
    setSelectedDriveItemToView(driveItem);
    setShowUsersModal(true);
  };
  const handleShowRenameModal = () => setShowRenameModal(true);
  const handleShowUploadedFileShareModal = (item: DriveItem) => {
    setSelectedFileForShare(item);
    setshowUploadedFileShareModal(true);
  };


  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortIndicator = (column: string) => {
    if (sortColumn !== column) {
      return <span className="text-[#6A7282]">⇅</span>;
    }
    return sortDirection === "asc" ? (
      <span className="text-[#6A7282]">▲</span>
    ) : (
      <span className="text-[#6A7282]">▼</span>
    );
  };

  const getItemIcon = (item: DriveItem) => {
    // If it's a folder
    if (item.isFolder) {
      return tabfoldericon;
    }

    // Get extension from file name
    const ext = item.name?.substring(item.name.lastIndexOf(".")).toLowerCase();

    // Return matching icon or fallback
    return extensionToIcon[ext] || fileicons; // default fallback to image icon
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner size="sm" /> Loading...
      </div>
    );
  }

  return (
    <>
      <div className={`common_content_inner  ${pagination.pageSize === 10 ? 'drive-page-size-10' : ''} drive-content-container`}>
        {" "}
        <div className="personal_flx">
          <div className="personal_flx_left">
            <div className="breadcrumb-container !w-full">
              <div
                className="drive-breadcrumb-container !w-full"
                style={{ cursor: "pointer" }}
              >
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={crumb.id ?? "root"}>
                    {index === 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none" style={{ display: "block" }}>
                          <path d="M13.7091 5.11997C13.5676 4.92451 13.3809 4.76608 13.1651 4.65815C12.9492 4.55022 12.7105 4.49597 12.4692 4.50001H12.3092V3.50008C12.3093 3.30056 12.2695 3.10303 12.1922 2.91907C12.115 2.73511 12.0018 2.56841 11.8594 2.42874C11.7169 2.28906 11.548 2.17921 11.3625 2.10562C11.1771 2.03203 10.9788 1.99618 10.7793 2.00017H7.53952C7.4183 1.99946 7.30065 1.95907 7.20454 1.88518L5.20467 0.325279C4.93095 0.114519 4.59519 0.000253803 4.24973 0.000300032H1.5299C1.33042 -0.00369037 1.13215 0.0321584 0.946699 0.105747C0.761246 0.179336 0.592338 0.289185 0.449866 0.428863C0.307394 0.568542 0.194222 0.735241 0.116975 0.919201C0.0397293 1.10316 -3.81466e-05 1.30068 1.76147e-06 1.5002V10.4996C-0.000432564 10.7764 0.0794602 11.0473 0.229987 11.2796C0.368331 11.4995 0.560071 11.6808 0.787358 11.8067C1.01464 11.9326 1.27009 11.9989 1.5299 11.9995H11.0293C11.3557 12.0077 11.6759 11.9091 11.9412 11.7187C12.2065 11.5283 12.4024 11.2565 12.4992 10.9446L13.9291 6.44489C13.9979 6.22214 14.0139 5.98642 13.9757 5.75643C13.9375 5.52645 13.8462 5.30853 13.7091 5.11997ZM0.999938 1.5002C0.999815 1.43195 1.01367 1.36438 1.04065 1.30168C1.06763 1.23898 1.10717 1.18247 1.15682 1.13563C1.20647 1.08879 1.26519 1.05261 1.32935 1.02933C1.39351 1.00604 1.46177 0.996141 1.5299 1.00024H4.24973C4.3726 1.00023 4.49207 1.04064 4.58971 1.11523L6.58958 2.67513C6.8614 2.88585 7.19559 3.00018 7.53952 3.00011H10.7793C10.8475 2.99601 10.9157 3.00591 10.9799 3.0292C11.044 3.05248 11.1028 3.08866 11.1524 3.1355C11.2021 3.18234 11.2416 3.23886 11.2686 3.30156C11.2955 3.36426 11.3094 3.43182 11.3093 3.50008V4.50001H3.30979C2.98588 4.49516 2.66911 4.5953 2.40685 4.78546C2.14459 4.97562 1.95095 5.24557 1.85488 5.55494L0.999938 8.26977V1.5002ZM12.9992 6.14491L11.5693 10.6446C11.5372 10.7509 11.4706 10.8435 11.3801 10.9078C11.2896 10.972 11.1802 11.0043 11.0693 10.9996H1.5699C1.45714 10.9993 1.34718 10.9644 1.25492 10.8996L2.85482 5.85493C2.88691 5.74864 2.95347 5.65607 3.044 5.5918C3.13452 5.52752 3.24387 5.49521 3.35479 5.49995H12.4692C12.5535 5.49936 12.6367 5.5185 12.7122 5.55583C12.7877 5.59316 12.8535 5.64765 12.9042 5.71494C12.9518 5.7746 12.9851 5.84437 13.0016 5.9189C13.018 5.99343 13.0172 6.07074 12.9992 6.14491Z" fill="#7E57C2" />
                        </svg>
                        <span
                          style={{
                            color: "#7E57C2",
                            textAlign: "center",
                            fontFamily: "Poppins",
                            fontSize: "1rem",
                            fontStyle: "normal",
                            fontWeight: 400,
                            lineHeight: "1.25rem",
                            display: "flex",
                            alignItems: "center"
                          }}
                          onClick={() => {
                            if (index < breadcrumbs.length - 1) {
                              navigate(`/drive/personal`);
                            }
                          }}
                        >
                          Drive
                        </span>
                      </div>
                    )}
                    {index > 0 && (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ display: "block", margin: "0 0.5rem" }}>
                          <path d="M6 12L10 8L6 4" stroke="#99A1AF" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span
                          style={{
                            color: "#364153",
                            fontFamily: "Poppins",
                            fontSize: "1rem",
                            fontStyle: "normal",
                            fontWeight: 400,
                            lineHeight: "1.25rem",
                            cursor: index === breadcrumbs.length - 1 ? "default" : "pointer",
                            display: "flex",
                            alignItems: "center"
                          }}
                          onClick={() => {
                            if (index < breadcrumbs.length - 1) {
                              navigate(`/drive/personal/${crumb.id}`);
                            }
                          }}
                        >
                          {crumb.name}
                        </span>
                      </>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            {itemStructure === "grid" && (
              <div className="personal_card_grid">
                {sortedAndFiltered.length === 0 ? (
                  <div className="no-participants-found-container">
                    <NoParticipantsFound />
                  </div>
                ) : (
                  sortedAndFiltered.map((item, index) => {
                    const itemIcon = getItemIcon(item);
                    return (
                      <div
                        className="personal_card cursor-pointer"
                        key={index}
                        onClick={() => handleItemClick(item)}
                      >
                        <div className="personal_card_cntent_flx">
                          <div className="personal_card_img flex-shrik-0">
                            <img
                              style={{ width: "1.5rem" }}
                              src={itemIcon}
                              alt="icon"
                            />
                          </div>
                          <div className="personal_card_cntent">
                            <h3>{item.name}</h3>
                            <p>{formatFileSize(item.size || 0)}</p>
                          </div>
                        </div>
                        <div
                          className="personal_card_action_btn"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          {/* {!item.isFolder && (
                            <span>
                              <img
                                src={sharelinkicon}
                                alt="trashicons"
                                onClick={() => {
                                  handleShowUploadedFileShareModal(item);
                                }}
                              />
                            </span>
                          )} */}
                          {!item.isFolder && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                openShareModal(item);
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <g clipPath="url(#clip0_1792_810)">
                                  <path d="M12 5.33334C13.1046 5.33334 14 4.43791 14 3.33334C14 2.22877 13.1046 1.33334 12 1.33334C10.8954 1.33334 10 2.22877 10 3.33334C10 4.43791 10.8954 5.33334 12 5.33334Z" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M4 10C5.10457 10 6 9.10457 6 8C6 6.89543 5.10457 6 4 6C2.89543 6 2 6.89543 2 8C2 9.10457 2.89543 10 4 10Z" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M12 14.6667C13.1046 14.6667 14 13.7712 14 12.6667C14 11.5621 13.1046 10.6667 12 10.6667C10.8954 10.6667 10 11.5621 10 12.6667C10 13.7712 10.8954 14.6667 12 14.6667Z" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M5.72656 9.00666L10.2799 11.66" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M10.2732 4.34L5.72656 6.99333" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                </g>
                                <defs>
                                  <clipPath id="clip0_1792_810">
                                    <rect width="16" height="16" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            </span>
                          )}
                          {/* {!item.isFolder && ( */}
                          {!item.isFolder && (
                            <UploadedFileShareModal
                              trigger={<span></span>}
                              open={openShareFileId === item.id}
                              onClose={closeShareModal}
                              authToken={authToken}
                              selectedFile={selectedFileForShare}
                              refreshCurrectFolder={refreshCurrentFolder}
                            />
                          )}
                          {/* )} */}
                          {/* <span>
                            <img
                              src={editicons}
                              alt="editicons"
                              onClick={() => {
                                setRenameTarget(item);
                                handleShowRenameModal();
                              }}
                            />
                          </span> */}
                          <span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none" onClick={(e) => {
                              handleDeleteItem(item);
                            }} style={{ cursor: 'pointer' }}>
                              <path d="M7.69922 8.46894V13.0884" stroke="#747474" strokeWidth="1.15486" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M10.7773 8.46894V13.0884" stroke="#747474" strokeWidth="1.15486" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M14.6263 4.61941V15.3981C14.6263 15.8064 14.4641 16.1981 14.1753 16.4869C13.8865 16.7756 13.4949 16.9379 13.0865 16.9379H5.38746C4.97908 16.9379 4.58743 16.7756 4.29866 16.4869C4.00989 16.1981 3.84766 15.8064 3.84766 15.3981V4.61941" stroke="#747474" strokeWidth="1.15486" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M2.30859 4.61941H16.1669" stroke="#747474" strokeWidth="1.15486" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M6.16016 4.61943V3.07962C6.16016 2.67124 6.32239 2.27958 6.61116 1.99081C6.89993 1.70204 7.29158 1.53981 7.69996 1.53981H10.7796C11.188 1.53981 11.5796 1.70204 11.8684 1.99081C12.1572 2.27958 12.3194 2.67124 12.3194 3.07962V4.61943" stroke="#747474" strokeWidth="1.15486" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
            {itemStructure === "list" && (
              <>
                <div className="drive-table-wrapper">
                <Table responsive className="drive-table">
                  <thead>
                    <tr>
                      {/* <th style={{ width: "4.625rem" }}></th> */}
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("name")}
                      >
                        Name {getSortIndicator("name")}
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("sharedWith")}
                      >
                        Shared With User {getSortIndicator("sharedWith")}
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("created")}
                      >
                        Created {getSortIndicator("created")}
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("updated")}
                      >
                        Last Updated {getSortIndicator("updated")}
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("size")}
                      >
                        File Size {getSortIndicator("size")}
                      </th>

                      <th className="action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAndFiltered.length === 0 ? (
                      <tr className="no-data-row">
                        <td colSpan={6} style={{ padding: 0, border: "none" }}>
                          <div className="no-participants-found-container">
                            <NoParticipantsFound />
                          </div>
                        </td>
                      </tr>
                    ) : (
                      sortedAndFiltered.map((item, index) => {
                        const sharedUsers = item.sharedWithUsers || [];
                        const itemIcon = getItemIcon(item);
                        return (
                          <tr
                            key={index}
                            className="cursor-pointer"
                            onClick={() => handleItemClick(item)}
                          >
                      <td>
                        <div className="tableicon-tab">
                          <span className="tableicon">
                            <img
                              style={{ width: "1.5rem" }}
                              src={itemIcon}
                              alt="folderlgicon"
                            />
                          </span>
                          <span className="font-normal text-[#354152] text-lg tracking-[0] leading-6 truncate">
                            {item.name}
                          </span>
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {!item.isFolder && sharedUsers.length > 0 && (
                          <div className="stack_img_flx">
                            {sharedUsers.slice(0, 3).map((u, index) => (
                              <div
                                key={index}
                                className="stack_img"
                                onClick={() =>
                                  navigate(`/profile/user/${u.id}`)
                                }
                                title={`${u.firstName} ${u.lastName}`}
                              >
                                <AvatarImage
                                  userId={u.id}
                                  size="lg"
                                  displayName={`${u.firstName} ${u.lastName}`}
                                />
                              </div>
                            ))}

                            {/* less than 3 → show ... */}
                            {sharedUsers.length <= 3 && (
                              <div
                                className="stack_img stack_img_txt"
                                onClick={() => handleShowUsersModal(item)}
                              >
                                <span>...</span>
                              </div>
                            )}

                            {/* more than 3 → show +n */}
                            {sharedUsers.length > 3 && (
                              <div
                                className="stack_img stack_img_txt"
                                onClick={() => handleShowUsersModal(item)}
                              >
                                <span>+{sharedUsers.length - 3}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      <td>
                        {item?.createdAt
                          ? (() => {
                            const localDate = convertUtcToLocalDate(item.createdAt);
                            return localDate ? localDate.toLocaleDateString() : "--";
                          })()
                          : "--"}
                      </td>
                      <td>
                        {item.isFolder
                          ? (item?.lastUpdatedDate || item?.updateDate)
                            ? (() => {
                              const localDate = convertUtcToLocalDate(item.lastUpdatedDate || item.updateDate);
                              return localDate ? localDate.toLocaleDateString() : "--";
                            })()
                            : "--"
                          : item?.createdAt
                            ? (() => {
                              const localDate = convertUtcToLocalDate(item.createdAt);
                              return localDate ? localDate.toLocaleDateString() : "--";
                            })()
                            : "--"}
                      </td>
                      <td>{formatFileSize(item.size || 0)}</td>

                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="action_btn">
                          {/* Select checkbox for files only */}
                          {!item.isFolder && (
                            <span className="action_checkbox" onClick={(e) => e.stopPropagation()} style={{ marginRight: '0.5rem' }}>
                              <InputGroup.Checkbox
                                type="checkbox"
                                checked={selectedFileIds.includes(item.id)}
                                onChange={() => toggleSelectFile(item.id)}
                                aria-label={`Select ${item.name}`}
                              />
                            </span>
                          )}

                          {/* Show ONLY for files */}

                          <span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none" onClick={() => {
                              setRenameTarget(item);
                              handleShowRenameModal();
                            }} style={{ cursor: 'pointer' }}>
                              <mask id="mask0_3965_5878" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="18" height="18">
                                <path d="M0 0H18V18H0V0Z" fill="white" />
                                <path d="M11.503 2.89681L2.87019 11.5296C2.63619 11.7636 2.51979 11.88 2.43819 12.0216C2.35899 12.1644 2.31819 12.324 2.23899 12.6456L1.44219 15.8304C1.33419 16.2624 1.28019 16.4796 1.40019 16.5996C1.52019 16.7196 1.73619 16.6656 2.16939 16.5576L5.35539 15.7608C5.67579 15.6816 5.83539 15.6408 5.97699 15.5604C6.11979 15.48 6.23619 15.3636 6.46899 15.1296L15.103 6.49681C15.9034 5.69761 16.303 5.29681 16.303 4.80001C16.303 4.30321 15.9034 3.90241 15.103 3.10321L14.8966 2.89681C14.0962 2.09761 13.6966 1.69681 13.1998 1.69681C12.703 1.69681 12.3022 2.09761 11.503 2.89681Z" fill="black" />
                              </mask>
                              <g mask="url(#mask0_3965_5878)">
                                <path d="M11.503 2.89681L2.87019 11.5296C2.63619 11.7636 2.51979 11.88 2.43819 12.0216C2.35899 12.1644 2.31819 12.324 2.23899 12.6456L1.44219 15.8304C1.33419 16.2624 1.28019 16.4796 1.40019 16.5996C1.52019 16.7196 1.73619 16.6656 2.16939 16.5576L5.35539 15.7608C5.67579 15.6816 5.83539 15.6408 5.97699 15.5604C6.11979 15.48 6.23619 15.3636 6.46899 15.1296L15.103 6.49681C15.9034 5.69761 16.303 5.29681 16.303 4.80001C16.303 4.30321 15.9034 3.90241 15.103 3.10321L14.8966 2.89681C14.0962 2.09761 13.6966 1.69681 13.1998 1.69681C12.703 1.69681 12.3022 2.09761 11.503 2.89681Z" stroke="#747474" strokeWidth="2" />
                              </g>
                              <path d="M10.1992 3L13.7992 0.599998L17.3992 4.2L14.9992 7.8L10.1992 3Z" fill="#747474" />
                            </svg>
                          </span>

                          <span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none" onClick={() => {
                              handleDeleteItem(item);
                            }} style={{ cursor: 'pointer' }}>
                              <path d="M7.69922 8.46894V13.0884" stroke="#747474" strokeWidth="1.15486" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M10.7773 8.46894V13.0884" stroke="#747474" strokeWidth="1.15486" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M14.6263 4.61941V15.3981C14.6263 15.8064 14.4641 16.1981 14.1753 16.4869C13.8865 16.7756 13.4949 16.9379 13.0865 16.9379H5.38746C4.97908 16.9379 4.58743 16.7756 4.29866 16.4869C4.00989 16.1981 3.84766 15.8064 3.84766 15.3981V4.61941" stroke="#747474" strokeWidth="1.15486" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M2.30859 4.61941H16.1669" stroke="#747474" strokeWidth="1.15486" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M6.16016 4.61943V3.07962C6.16016 2.67124 6.32239 2.27958 6.61116 1.99081C6.89993 1.70204 7.29158 1.53981 7.69996 1.53981H10.7796C11.188 1.53981 11.5796 1.70204 11.8684 1.99081C12.1572 2.27958 12.3194 2.67124 12.3194 3.07962V4.61943" stroke="#747474" strokeWidth="1.15486" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                          {!item.isFolder && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                openShareModal(item);
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <g clipPath="url(#clip0_1792_810)">
                                  <path d="M12 5.33334C13.1046 5.33334 14 4.43791 14 3.33334C14 2.22877 13.1046 1.33334 12 1.33334C10.8954 1.33334 10 2.22877 10 3.33334C10 4.43791 10.8954 5.33334 12 5.33334Z" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M4 10C5.10457 10 6 9.10457 6 8C6 6.89543 5.10457 6 4 6C2.89543 6 2 6.89543 2 8C2 9.10457 2.89543 10 4 10Z" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M12 14.6667C13.1046 14.6667 14 13.7712 14 12.6667C14 11.5621 13.1046 10.6667 12 10.6667C10.8954 10.6667 10 11.5621 10 12.6667C10 13.7712 10.8954 14.6667 12 14.6667Z" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M5.72656 9.00666L10.2799 11.66" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M10.2732 4.34L5.72656 6.99333" stroke="#747474" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                </g>
                                <defs>
                                  <clipPath id="clip0_1792_810">
                                    <rect width="16" height="16" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            </span>
                          )}
                          {!item.isFolder && (
                            <UploadedFileShareModal
                              trigger={<span></span>}
                              open={openShareFileId === item.id}
                              onClose={closeShareModal}
                              authToken={authToken}
                              selectedFile={selectedFileForShare}
                              refreshCurrectFolder={refreshCurrentFolder}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                      );
                    })
                    )}
                  </tbody>
                </Table>
                </div>
              </>
            )}
          </div>
          {/* User Storage */}
          {/* {authToken && (
            <DriveStorage
              authToken={authToken}
              refreshKey={storageRefreshKey}
            />
          )} */}
        </div>
      </div>
      {/* File Upload Modal */}
      {showFileModal && (
        <FileUploadModal
          handleCloseFileModal={handleCloseFileModal}
          showFileModal={showFileModal}
          authToken={authToken}
          currentFolderId={currentFolder?.id}
          refreshCurrentFolder={refreshCurrentFolder}
        />
      )}
      {/* Create Folder Modal */}
      {showFolderModal && (
        <FolderUploadModal
          handleCloseFolderModal={handleCloseFolderModal}
          showFolderModal={showFolderModal}
          token={authToken}
          currentFolderId={currentFolder?.id}
          refreshCurrentFolder={() => {
            refreshCurrentFolder();
            // fetchRootLevelFiles(); // Refresh root files after folder creation
          }}
          availableFiles={rootLevelFiles.map((item) => ({
            id: item.id,
            name: item.name,
            size: formatFileSize(item.size || 0),
            timeAgo: item.updateDate
              ? getTimeAgo(
                item.updateDate instanceof Date
                  ? item.updateDate
                  : new Date(item.updateDate)
              )
              : "Unknown",
          }))}
        />
      )}
      {/*  Shared File Modal */}
      {showSharedFileModal && (
        <ShareFileModal
          handleCloseSharedFileModal={handleCloseSharedFileModal}
          showSharedFileModal={showSharedFileModal}
          authToken={authToken}
          currentUserId={currentUser?.uid}
          refreshCurrentFolder={refreshCurrentFolder}
        />
      )}

      {/* shared uploaded file modal */}
      {/* {showUploadedFileShareModal && shelectedFileForShare && authToken && (
        <UploadedFileShareModal
          authToken={authToken}
          handleCloseUploadedFileShareModal={handleCloseUploadedFileShareModal}
          selectedFile={shelectedFileForShare}
          showUploadedFileShareModal={showUploadedFileShareModal}
          refreshCurrectFolder={refreshCurrentFolder}
        />
      )} */}
      {/* {isShareModalOpen && selectedFileForShare && (
        <UploadedFileShareModal
          open={isShareModalOpen}
          onClose={closeShareModal}
          authToken={authToken}
          selectedFile={selectedFileForShare}
          position={sharePosition}
          refreshCurrectFolder={refreshCurrentFolder}
        />
      )} */}
      {/* Rename Modal */}
      {showRenameModal && renameTarget && (
        <RenameFileFolder
          handleCloseRenamerModal={handleCloseRenamerModal}
          renameTarget={renameTarget}
          showRenameModal={showRenameModal}
          authToken={authToken}
          refreshCurrentFolder={refreshCurrentFolder}
          parentFolderId={Number(folderId)}
        />
      )}

      {/* Show UserList Modal */}
      {showUsersModal && selectedDriveItemToView && (
        <ShowUsersModal
          handleCloseUsersModal={handleCloseUsersModal}
          driveItem={selectedDriveItemToView}
          showUsersModal={showUsersModal}
          authToken={authToken}
          refreshCurrentFolder={refreshCurrentFolder}
        />
      )}

    </>
  );
}

export default PersonalDrive;
